import { useState, useEffect, useCallback, useMemo } from 'react';
import Icon from './Icons';

export default function ReviewQueueShell({ items, renderItem, onComplete, emptyState, label }) {
  const [idx, setIdx] = useState(0);

  // Only reset position when the set of item IDs changes (queue replaced),
  // not when item properties like flag_count are updated in place.
  const itemIds = useMemo(() => items.map(i => i.id).join(','), [items]);
  useEffect(() => { setIdx(0); }, [itemIds]);

  const total = items.length;

  const goNext = useCallback(() => {
    setIdx(i => {
      const next = i + 1;
      if (next >= total) {
        onComplete?.();
        return i;
      }
      return next;
    });
  }, [total, onComplete]);

  const goPrev = useCallback(() => {
    setIdx(i => Math.max(0, i - 1));
  }, []);

  const jumpTo = useCallback((n) => {
    setIdx(Math.max(0, Math.min(total - 1, n)));
  }, [total]);

  useEffect(() => {
    function onKey(e) {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;
      if (e.target.classList.contains('health-chip')) return;
      if (e.key === 'ArrowLeft')  { e.preventDefault(); goPrev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev]);

  if (total === 0) {
    return emptyState || null;
  }

  const current = items[Math.min(idx, total - 1)];

  return (
    <div>
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        padding: '0.5rem 1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
      }}>
        <button
          type="button"
          className="icon-btn"
          onClick={goPrev}
          disabled={idx === 0}
          aria-label="Previous"
          title="Previous (←)"
        >
          <Icon name="chevronLeft" size={18} />
        </button>
        <span style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text)', flex: 1, textAlign: 'center' }}>
          {label} — {idx + 1} of {total}
        </span>
        <button
          type="button"
          className="icon-btn"
          onClick={goNext}
          disabled={idx >= total - 1}
          aria-label="Next"
          title="Next (→)"
        >
          <Icon name="chevronRight" size={18} />
        </button>
      </div>
      {renderItem(current, { goNext, goPrev, idx, jumpTo, total })}
    </div>
  );
}
